import { z } from 'zod';

import type { PublicGame } from '~/domains/contest/domains/services/interfaces/propsService/api/game';
import type { PublicTeam } from '~/domains/contest/domains/services/interfaces/propsService/api/team';
import type { PublicEntity } from '~/domains/contest/domains/services/interfaces/propsService/api/entity';

import type { MarketResult } from './market.transaction.types';

export type MarketCategoryType = 'gameline' | 'player_props' | 'team_props' | 'game_props';
export type MarketGroupType = 'gameline' | 'future_team_champion';

export const MARKET_SUB_CATEGORIES = [
  'nfl',
  'nba',
  'mlb',
  'nhl',
  'ncaaf',
  'ncaab',
  'wnba',
  'mls',
  'epl',
] as const;

export type MarketSubCategory = (typeof MARKET_SUB_CATEGORIES)[number];

export const isMarketSubCategory = (value: unknown): value is MarketSubCategory =>
  typeof value === 'string' && MARKET_SUB_CATEGORIES.some((subCategory) => subCategory === value);

export type PredictionMarkets = {
  id: string;
  title: string;
  sub_category: MarketSubCategory;
  event_id: string;
  event_start: number;
  status: string;
};

export const GetPredictionMarketsDiscoverSchema = z.object({
  sub_category: z
    .string()
    .transform((val) => val.split(',').map((s) => s.trim()))
    .pipe(z.array(z.enum(MARKET_SUB_CATEGORIES)))
    .optional(),
  search: z.string().optional(),
  offset: z.coerce.number().min(0).default(0).optional(),
  limit: z.coerce.number().min(1).max(50).default(50).optional(),
});

export const GetPredictionMarketSchema = z.object({
  id: z.string(),
});

export const CreateAccountResponseSchema = z.object({
  user_id: z.string(),
});

export const CreateAccountPayloadSchema = z.object({
  phone_number: z.string().min(7).max(7),
  area_code: z.string().min(3).max(3),
  ssn: z.string().min(9).max(9),
  country_code: z.string().min(1).max(2),
});

export type PutKycPayload = {
  first_name: string;
  last_name: string;
  birth_date: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  country_code: string;
  area_code: string;
  phone_number: string;
  ssn: string;
};

export const KYCStatusResponseSchema = z.object({
  access_level: z.enum(['applicant', 'member']),
  kyc_failure_reason: z.string().optional(),
  trading_blocked: z.boolean().optional(),
});

export type PredictionMarketWithMarketGroup = PredictionMarkets & {
  market_group: { category_type: MarketCategoryType };
};

export type PublicMarketGroupGamelineSection = {
  type: 'gameline';
  title: string;
  sub_category: MarketSubCategory;
  market_groups: PublicMarketGroup[];
};

export type PublicPredictionMarket =
  | PublicMarketGamelineMoneyline
  | PublicMarketGamelineTotal
  | PublicMarketGamelineSpread
  | PublicMarketFutureTeamChampion
  | PublicMarketPlayerProp;

export type PublicMarketGroupGameline = {
  id: string;
  sub_category: MarketSubCategory;
  event_id: string;
  event_start?: number;
  type: 'gameline';
  markets: PublicPredictionMarket[];
  alternate_markets: PublicPredictionMarket[];
};

export type PublicMarketGroupFutureTeamChampion = {
  id: string;
  sub_category: MarketSubCategory;
  type: 'future_team_champion';
  title?: string;
  icon_url?: string;
  markets: PublicPredictionMarket[];
  alternate_markets?: PublicPredictionMarket[];
};

export type PublicMarketGroup = PublicMarketGroupGameline | PublicMarketGroupFutureTeamChampion;

export type MarketPrice = {
  american: string;
  decimal: string;
  percentage: string;
  price: string;
  numeric_price?: number;
};

export type OrderbookOrder = {
  price: string;
  quantity: number;
};

export type Orderbook = {
  yes_dollars: OrderbookOrder[];
  no_dollars: OrderbookOrder[];
};

export type PublicMarketGamelineMoneylineBase = {
  id: string;
  title: string;
  sub_category: MarketSubCategory;
  type: 'moneyline';
  status?: MarketStatus;
  event_id: string;
  event_start: number;
  price_yes: MarketPrice;
  price_no: MarketPrice;
  last_price_yes?: number | null;
  last_price_no?: number | null;
  volume?: number;
  yes_liquidity?: number;
  no_liquidity?: number;
  orderbook?: Orderbook;
  rules?: string;
};

export type PublicMarketGamelineMoneylineOneWay = PublicMarketGamelineMoneylineBase & {
  moneyline_type: 'one_way';
  team_id: string;
};

export type PublicMarketGamelineMoneylineTwoWay = PublicMarketGamelineMoneylineBase & {
  moneyline_type: 'two_way';
  yes_team_id: string;
  no_team_id: string;
};

export type PublicMarketGamelineMoneyline =
  PublicMarketGamelineMoneylineOneWay | PublicMarketGamelineMoneylineTwoWay;

export function isMoneylineTwoWay(
  m: PublicMarketGamelineMoneyline
): m is PublicMarketGamelineMoneylineTwoWay {
  return m.moneyline_type === 'two_way';
}

export function isMoneylineOneWay(
  m: PublicMarketGamelineMoneyline
): m is PublicMarketGamelineMoneylineOneWay {
  return m.moneyline_type !== 'two_way';
}

export function normalizeMoneylineMarket(market: PublicPredictionMarket): PublicPredictionMarket {
  if (market.type !== 'moneyline') return market;
  if ('moneyline_type' in market && market.moneyline_type) return market;
  return { ...market, moneyline_type: 'one_way' } as PublicPredictionMarket;
}

export function normalizeMarketGroupMarkets<
  T extends { markets: PublicPredictionMarket[]; alternate_markets?: PublicPredictionMarket[] },
>(group: T): T & { alternate_markets: PublicPredictionMarket[] } {
  return {
    ...group,
    markets: group.markets.map(normalizeMoneylineMarket),
    alternate_markets: (group.alternate_markets ?? []).map(normalizeMoneylineMarket),
  };
}

export type PublicMarketGamelineTotal = {
  id: string;
  title: string;
  sub_category: MarketSubCategory;
  type: 'total';
  status?: MarketStatus;
  event_id: string;
  event_start: number;
  line: number;
  yes_line_display: string;
  no_line_display: string;
  price_yes: MarketPrice;
  price_no: MarketPrice;
  last_price_yes?: number | null;
  last_price_no?: number | null;
  volume?: number;
  yes_team_id: string;
  no_team_id: string;
  yes_liquidity?: number;
  no_liquidity?: number;
  orderbook?: Orderbook;
};

export type PublicMarketGamelineSpread = {
  id: string;
  title: string;
  sub_category: MarketSubCategory;
  type: 'spread';
  status?: MarketStatus;
  event_id: string;
  team_id: string;
  event_start: number;
  line: number;
  yes_line_display: string;
  no_line_display: string;
  yes_team_id: string;
  no_team_id: string;
  price_yes: MarketPrice;
  price_no: MarketPrice;
  last_price_yes?: number | null;
  last_price_no?: number | null;
  volume?: number;
  yes_liquidity?: number;
  no_liquidity?: number;
  orderbook?: Orderbook;
};

export type PublicMarketFutureTeamChampion = {
  id: string;
  title: string;
  sub_category: MarketSubCategory;
  type: 'future_team_champion';
  team_id: string;
  event_start: number;
  yes_line_display: string;
  no_line_display: string;
  price_yes: MarketPrice;
  price_no: MarketPrice;
  last_price_yes?: number | null;
  last_price_no?: number | null;
  volume?: number;
  status?: MarketStatus;
  updated_at?: number;
  rules?: string;
  market_group_id: string;
  yes_liquidity?: number;
  no_liquidity?: number;
  orderbook?: Orderbook;
};

export type PublicPlayerPropLine = {
  id: string;
  line?: number;
  status?: MarketStatus;
  yes_line_display: string;
  no_line_display: string;
  price_yes: MarketPrice;
  price_no: MarketPrice;
  last_price_yes?: number | null;
  last_price_no?: number | null;
  yes_liquidity?: number;
  no_liquidity?: number;
  rules?: string;
  updated_at?: number;
};

export type PublicMarketPlayerProp = {
  id: string;
  title: string;
  sub_category: MarketSubCategory;
  type: 'player_prop';
  status?: MarketStatus;
  event_id: string;
  event_start: number;
  entity_id: string;
  team_id: string;
  prop_type: string;
  prop_type_display: string;
  line?: number;
  yes_line_display: string;
  no_line_display: string;
  price_yes: MarketPrice;
  price_no: MarketPrice;
  last_price_yes?: number | null;
  last_price_no?: number | null;
  volume?: number;
  yes_liquidity?: number;
  no_liquidity?: number;
  orderbook?: Orderbook;
  rules?: string;
  alternate_lines?: PublicPlayerPropLine[];
  live_stat_key?: string;
  live_stat?: number | null;
  market_group_id?: string;
  updated_at?: number;
};

export type PublicPlayerPropSection = {
  prop_type: string;
  title: string;
  team_id?: string;
  total: number;
  has_more: boolean;
  next_offset?: number;
  markets: PublicMarketPlayerProp[];
};

export type PlayerPropMarketsResponse = {
  data: PublicPlayerPropSection[];
  included: MarketIncluded;
  status: string;
};

export type PublicPlayerPropGroup = {
  id: string;
  type: 'player_prop';
  title: string;
  icon_url?: string | null;
  entity_id: string;
  team_id: string;
  event_id: string;
  event_start: number;
  sub_category: MarketSubCategory;
  markets: PublicMarketPlayerProp[];
};

export type PlayerPropListingResponse = {
  data: PublicPlayerPropGroup[];
  included: MarketIncluded;
  total: number;
  offset: number;
  limit: number;
  status: string;
};

export type GetPlayerPropSectionParams = {
  eventId: string;
  propType: string;
  teamId?: string;
  limit?: number;
  offset?: number;
};

/** A league screen tab: `title` labels it, `types` selects the market groups it shows. */
export type MarketFilter = {
  title: string;
  types: MarketGroupType[];
};

export type PublicMarketSubCategory = {
  id: MarketSubCategory;
  name: string;
  icon_url: string;
  active: boolean;
  filters?: MarketFilter[];
};

export type PublicIncludedSubCategory = {
  id: MarketSubCategory;
  name?: string;
  icon_url?: string;
};

export type MarketIncluded = {
  events: PublicGame[];
  teams: PublicTeam[];
  entities: PublicEntity[];
  sub_categories?: PublicIncludedSubCategory[];
};

export type MarketSubCategoriesResponse = {
  sub_categories: PublicMarketSubCategory[];
  status: string;
};

export type PredictionMarketDiscoverResponse = {
  discover: PublicMarketGroupGamelineSection[];
  included: MarketIncluded;
  status: string;
};

export type PublicGameTabFilter = {
  title: string;
  types: string[];
};

export type PublicGameTab = {
  title: string;
  types: string[];
  group_by?: string;
  filters?: PublicGameTabFilter[];
};

export type MarketGroupDetailResponse = {
  market_group: PublicMarketGroup & {
    event_start: number;
  };
  filters?: PublicGameTab[];
  included: MarketIncluded;
  status: string;
};

export type PlayerPropGroupDetailResponse = {
  market_group: PublicPlayerPropGroup;
  included: MarketIncluded;
  status: string;
};

export type GetPredictionMarketsDiscoverParams = z.infer<typeof GetPredictionMarketsDiscoverSchema>;
export type GetPredictionMarketParams = z.infer<typeof GetPredictionMarketSchema>;
export type CreateAccountPayload = z.infer<typeof CreateAccountPayloadSchema>;
export type CreateAccountResponse = z.infer<typeof CreateAccountResponseSchema>;
export type KYCStatusResponse = z.infer<typeof KYCStatusResponseSchema>;

export type GetPredictionMarketsParams = {
  sub_category: MarketSubCategory;
  types?: MarketGroupType[];
  offset?: number;
  limit?: number;
};

export type PredictionMarketsResponse = {
  data: PublicMarketGroup[];
  included: MarketIncluded;
  total: number;
  offset: number;
  limit: number;
  status: string;
};

export type GetPromotedMarketsParams = {
  types?: MarketGroupType[];
  limit?: number;
};

export type PortfolioSummaryResponse = {
  summary: {
    value: number;
    cost_basis: number;
  };
  status: 'success';
};

export type MarketStatus = 'active' | 'expired' | 'settled';

export type {
  MarketResult,
  TransactionEffect,
  PublicOrder,
  PublicSettlement,
  PublicOrderTransaction,
  PublicSettlementTransaction,
  PublicTransaction,
  GetMarketTransactionsParams,
  MarketTransactionsResponse,
} from './market.transaction.types';

export type {
  PusherPriceLevel,
  PusherOrderbook,
  PusherMarketUpdatePayload,
} from './market.pusher.types';
